import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import API_BASE_URL from '../config/api';
import '../styles/QuizGame.css';

const QuizGame = ({ user }) => {
  const [quiz, setQuiz] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateQuiz, setShowCreateQuiz] = useState(false);
  const [availableQuizzes, setAvailableQuizzes] = useState([]);
  const [newQuiz, setNewQuiz] = useState({
    title: '',
    questions: [{ question: '', options: ['', '', '', ''], correct: 0 }]
  });

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/quiz/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableQuizzes(data);
      }
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/quiz/${quizId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        fetchQuizzes();
        alert('Quiz deleted successfully! 💔');
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.message || 'Unable to delete quiz'}`);
      }
    } catch (error) {
      console.error('Error deleting quiz:', error);
      alert('Error deleting quiz. Please try again.');
    }
  };

  const startQuiz = selectedQuiz => {
    setQuiz(selectedQuiz);
    setCurrentQuestionIndex(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setQuizCompleted(false);
  };

  const handleAnswerSelect = answerIndex => {
    setSelectedAnswer(answerIndex);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return;

    const currentQuestion = quiz.questions[currentQuestionIndex];
    const isCorrect = selectedAnswer === currentQuestion.correct;
    if (isCorrect) setScore(score + 1);

    setShowResult(true);

    setTimeout(() => {
      if (currentQuestionIndex < quiz.questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setSelectedAnswer(null);
        setShowResult(false);
      } else {
        setQuizCompleted(true);
        submitQuizScore(score + (isCorrect ? 1 : 0));
      }
    }, 2000);
  };

  const submitQuizScore = async finalScore => {
    try {
      await fetch(`${API_BASE_URL}/api/quiz/score`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          score: finalScore,
          totalQuestions: quiz.questions.length,
          quizId: quiz._id
        })
      });
    } catch (error) {
      console.error('Error submitting score:', error);
    }
  };

  const addQuestion = () => {
    setNewQuiz({
      ...newQuiz,
      questions: [
        ...newQuiz.questions,
        { question: '', options: ['', '', '', ''], correct: 0 }
      ]
    });
  };

  const updateQuestion = (index, field, value) => {
    const updatedQuestions = [...newQuiz.questions];
    if (field === 'question') {
      updatedQuestions[index].question = value;
    } else if (field.startsWith('option')) {
      const optionIndex = parseInt(field.replace('option', ''), 10);
      updatedQuestions[index].options[optionIndex] = value;
    } else if (field === 'correct') {
      updatedQuestions[index].correct = parseInt(value, 10);
    }
    setNewQuiz({ ...newQuiz, questions: updatedQuestions });
  };

  const removeQuestion = index => {
    if (newQuiz.questions.length > 1) {
      const updated = newQuiz.questions.filter((_, i) => i !== index);
      setNewQuiz({ ...newQuiz, questions: updated });
    }
  };

  const saveQuiz = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/quiz/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newQuiz)
      });
      if (response.ok) {
        setShowCreateQuiz(false);
        setNewQuiz({
          title: '',
          questions: [{ question: '', options: ['', '', '', ''], correct: 0 }]
        });
        fetchQuizzes();
        alert('Quiz created successfully! 💕');
      } else {
        const errData = await response.json();
        alert(`Error: ${errData.message || 'Unable to create quiz'}`);
      }
    } catch (error) {
      console.error('Error creating quiz:', error);
      alert('Error creating quiz. Please try again.');
    }
  };

  const restartQuiz = () => {
    setQuiz(null);
    setCurrentQuestionIndex(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setQuizCompleted(false);
  };

  if (loading) {
    return (
      <div className="quiz-game">
        <div className="loading">Loading quizzes... 💕</div>
        <Navigation />
      </div>
    );
  }

  if (showCreateQuiz) {
    return (
      <div className="quiz-game">
        <div className="container">
          <div className="quiz-header">
            <h1>Create New Quiz</h1>
            <p>Add questions for you and your partner</p>
          </div>
          <div className="create-quiz-form">
            <div className="form-group">
              <input
                type="text"
                placeholder="Quiz Title"
                value={newQuiz.title}
                onChange={e => setNewQuiz({ ...newQuiz, title: e.target.value })}
                className="quiz-title-input"
              />
            </div>
            {newQuiz.questions.map((q, qi) => (
              <div key={qi} className="question-builder">
                <div className="question-header">
                  <h4>Question {qi + 1}</h4>
                  {newQuiz.questions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeQuestion(qi)}
                      className="remove-question-btn"
                    >
                      ❌
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="Enter your question"
                  value={q.question}
                  onChange={e => updateQuestion(qi, 'question', e.target.value)}
                  className="question-input"
                />
                <div className="options-builder">
                  {q.options.map((opt, oi) => (
                    <div key={oi} className="option-builder">
                      <input
                        type="radio"
                        name={`correct-${qi}`}
                        checked={q.correct === oi}
                        onChange={() => updateQuestion(qi, 'correct', oi)}
                      />
                      <input
                        type="text"
                        placeholder={`Option ${oi + 1}`}
                        value={opt}
                        onChange={e => updateQuestion(qi, `option${oi}`, e.target.value)}
                        className="option-input"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="quiz-actions">
              <button onClick={addQuestion} className="btn btn-secondary">
                ➕ Add Question
              </button>
              <button
                onClick={saveQuiz}
                className="btn btn-primary"
                disabled={
                  !newQuiz.title || newQuiz.questions.some(q => !q.question)
                }
              >
                💾 Save Quiz
              </button>
              <button
                onClick={() => setShowCreateQuiz(false)}
                className="btn btn-outline"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        <Navigation />
      </div>
    );
  }

  if (quizCompleted) {
    const percentage = Math.round((score / quiz.questions.length) * 100);
    return (
      <div className="quiz-game">
        <div className="container">
          <div className="quiz-completed">
            <div className="completion-icon">
              {percentage >= 80 ? '🏆' : percentage >= 60 ? '😊' : '💕'}
            </div>
            <h2>Quiz Completed!</h2>
            <div className="final-score">
              <h3>Your Score</h3>
              <div className="score-display">
                {score} / {quiz.questions.length}
              </div>
              <div className="percentage">{percentage}%</div>
            </div>
            <div className="result-message">
              {percentage >= 80 && <p>Amazing! You know each other so well! 💕</p>}
              {percentage >= 60 && percentage < 80 && (
                <p>Great job! You're getting to know each other better! 😊</p>
              )}
              {percentage < 60 && (
                <p>Keep learning about each other! Love grows stronger! 💖</p>
              )}
            </div>
            <button onClick={restartQuiz} className="btn btn-primary">
              Back to Quizzes
            </button>
          </div>
        </div>
        <Navigation />
      </div>
    );
  }

  if (quiz) {
    const current = quiz.questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / quiz.questions.length) * 100;
    return (
      <div className="quiz-game">
        <div className="container">
          <div className="quiz-progress">
            <div className="progress-info">
              <span className="score">🏆 Score: {score}</span>
              <span className="question-counter">
                Question {currentQuestionIndex + 1} of {quiz.questions.length}
              </span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="question-card">
            <div className="question-text">
              <h3>{current.question}</h3>
            </div>
            <div className="options">
              {current.options.map((opt, idx) => (
                <button
                  key={idx}
                  className={`option ${
                    selectedAnswer === idx ? 'selected' : ''
                  } ${
                    showResult
                      ? idx === current.correct
                        ? 'correct'
                        : selectedAnswer === idx
                        ? 'incorrect'
                        : ''
                      : ''
                  }`}
                  onClick={() => !showResult && handleAnswerSelect(idx)}
                  disabled={showResult}
                >
                  <span className="option-letter">
                    {String.fromCharCode(65 + idx)}
                  </span>
                  {opt}
                </button>
              ))}
            </div>
            {!showResult ? (
              <button
                onClick={handleSubmitAnswer}
                disabled={selectedAnswer === null}
                className="btn btn-primary submit-btn"
              >
                Submit Answer
              </button>
            ) : (
              <div className="result-feedback">
                {selectedAnswer === current.correct ? (
                  <div className="correct-feedback">
                    <span className="feedback-icon">🎉</span>
                    <p>Correct! You know your partner well! 💕</p>
                  </div>
                ) : (
                  <div className="incorrect-feedback">
                    <span className="feedback-icon">💔</span>
                    <p>
                      Not quite right, but that's okay! Keep learning about each
                      other! 💖
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <Navigation />
      </div>
    );
  }

  return (
    <div className="quiz-game">
      <div className="container">
        <div className="quiz-header">
          <div className="heart-decoration">💖</div>
          <h1>Love Quiz Game</h1>
          <p className="subtitle">✨ Test your knowledge about each other ✨</p>
        </div>
        <div className="quiz-actions-header">
          <button
            onClick={() => setShowCreateQuiz(true)}
            className="btn btn-primary"
          >
            ➕ Create New Quiz
          </button>
        </div>
        <div className="quiz-list">
          {availableQuizzes.length > 0 ? (
            availableQuizzes.map(qItem => (
              <div key={qItem._id} className="quiz-card">
                <div className="quiz-info">
                  <h3>{qItem.title}</h3>
                  <p>{qItem.questions.length} questions</p>
                  <p className="quiz-creator">
                    By: {qItem.createdBy?.name || qItem.createdBy || 'Unknown'}
                  </p>
                </div>
                <div className="quiz-actions">
                  <button
                    onClick={() => startQuiz(qItem)}
                    className="btn btn-secondary"
                  >
                    Start Quiz
                  </button>
                  {(qItem.createdBy?._id === user?.id || qItem.createdBy === user?.id) && (
                    <button
                      onClick={() => handleDeleteQuiz(qItem._id)}
                      className="btn btn-danger"
                      style={{ marginLeft: '10px' }}
                    >
                      🗑️ Delete
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <div className="heart-icon">💔</div>
              <h2>No Quizzes Yet</h2>
              <p>Create your first quiz to get started!</p>
              <button
                onClick={() => setShowCreateQuiz(true)}
                className="btn btn-primary"
              >
                Create Quiz
              </button>
            </div>
          )}
        </div>
      </div>
      <Navigation />
    </div>
  );
};

export default QuizGame;
